import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { packageData } from '../utils/packageData';
import { Package, StayType } from '../types';
import {
  getAvailableGroupSizes,
  getDiscountPercentage,
  getMinimumGroupSize,
  getPricePerPerson,
  getTotalPrice,
  hasStayTypeSelector,
} from '../utils/pricing';
import '../styling/PackageDetails.css';

const PackageDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [currentMediaIndex, setCurrentMediaIndex] = useState<number>(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [numberOfPeople, setNumberOfPeople] = useState<number>(2);
  const [stayType, setStayType] = useState<StayType>('standard');
  const [isQuoteFormOpen, setIsQuoteFormOpen] = useState<boolean>(false);
  const [quoteFormData, setQuoteFormData] = useState({
    startDate: '',
    duration: '',
    children: '',
    toddler: ''
  });

  // In a real app, you would fetch the package data based on the id
  const packageInfo: Package | undefined = packageData.find(pkg => pkg.id === parseInt(id || '0'));
  const mediaItems: string[] = packageInfo?.images ?? [];
  const showStaySelector = hasStayTypeSelector(packageInfo?.pricing);

  useEffect(() => {
    if (packageInfo?.pricing) {
      const minGroupSize = getMinimumGroupSize(packageInfo.pricing, showStaySelector ? stayType : undefined);
      setNumberOfPeople(minGroupSize);
    }
  }, [packageInfo, stayType, showStaySelector]);

  const getAvailableGroupSizesForPackage = (): number[] => {
    return getAvailableGroupSizes(packageInfo?.pricing, showStaySelector ? stayType : undefined);
  };

  const getNextGroupSize = (): number | null => {
    const availableSizes = getAvailableGroupSizesForPackage();
    const currentIndex = availableSizes.indexOf(numberOfPeople);
    return currentIndex < availableSizes.length - 1 ? availableSizes[currentIndex + 1] : null;
  };

  const getPreviousGroupSize = (): number | null => {
    const availableSizes = getAvailableGroupSizesForPackage();
    const currentIndex = availableSizes.indexOf(numberOfPeople);
    return currentIndex > 0 ? availableSizes[currentIndex - 1] : null;
  };

  const calculateTotalPrice = (): number => {
    return getTotalPrice(packageInfo?.pricing, numberOfPeople, showStaySelector ? stayType : undefined);
  };

  const calculatePricePerPerson = (): number => {
    return getPricePerPerson(packageInfo?.pricing, numberOfPeople, showStaySelector ? stayType : undefined);
  };

  const getDiscountPercentageForPackage = (): number => {
    return getDiscountPercentage(packageInfo?.pricing, numberOfPeople, showStaySelector ? stayType : undefined);
  };

  const isVideo = (url: string) => /\.(mp4|webm|ogg)$/i.test(url);

  // Handle quote form input changes
  const handleQuoteFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setQuoteFormData({
      ...quoteFormData,
      [e.target.name]: e.target.value
    });
  };

  // Handle quote form submission
  const handleQuoteSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const message = `Hey Travelisk! I'm interested in getting a quote for a travel package.

*Travel Details:*
Destination: ${packageInfo?.location || 'Meghalaya'}
Package: ${packageInfo?.title || 'Package'}
Start Date: ${quoteFormData.startDate}
Duration: ${quoteFormData.duration}

*Group Size:*
Adults (Above 18 years): ${numberOfPeople}
Children (Above 5 years): ${quoteFormData.children || '0'}
Toddler (Below 5 years): ${quoteFormData.toddler || '0'}
${showStaySelector ? `\nStay Type: ${stayType.charAt(0).toUpperCase() + stayType.slice(1)}` : ''}

*Pricing Details:*
Price per person: ₹${calculatePricePerPerson().toLocaleString()}
Total price for ${numberOfPeople} adults: ₹${calculateTotalPrice().toLocaleString()}

Please provide me with a customized quote for this trip. Thank you!`;

    const encodedMessage = encodeURIComponent(message);
    
    window.open(`https://wa.me/7980013170?text=${encodedMessage}`, '_blank');
    
    // Close form and reset data
    setIsQuoteFormOpen(false);
    setQuoteFormData({
      startDate: '',
      duration: '',
      children: '',
      toddler: ''
    });
  };

  // Open quote form
  const openQuoteForm = () => {
    setIsQuoteFormOpen(true);
  };

  // Close quote form
  const closeQuoteForm = () => {
    setIsQuoteFormOpen(false);
    setQuoteFormData({
      startDate: '',
      duration: '',
      children: '',
      toddler: ''
    });
  };

  const openLightbox = (index: number) => {
    setCurrentMediaIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => setIsLightboxOpen(false);

  const showPrev = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (mediaItems.length === 0) return;
    setCurrentMediaIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  const showNext = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (mediaItems.length === 0) return;
    setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
  };

  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      if (e.key === 'ArrowLeft') {
        if (mediaItems.length === 0) return;
        setCurrentMediaIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
      }
      if (e.key === 'ArrowRight') {
        if (mediaItems.length === 0) return;
        setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isLightboxOpen, mediaItems.length]);

  if (!packageInfo) {
    return (
      <div className="page-container">
        <h2>Package not found</h2>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="image-gallery">
        {mediaItems.map((url, index) => (
          isVideo(url) ? (
            <button
              key={index}
              className="gallery-thumb"
              onClick={() => openLightbox(index)}
              aria-label={`${packageInfo.title} video ${index + 1}`}
            >
              <div className="gallery-video-wrap">
                <video
                  className="gallery-image gallery-video"
                  src={url}
                  muted
                  playsInline
                  preload="metadata"
                />
                <span className="gallery-play" aria-hidden="true">▶</span>
              </div>
            </button>
          ) : (
            <img
              key={index}
              className="gallery-image"
              src={url}
              alt={`${packageInfo.title} ${index + 1}`}
              onClick={() => openLightbox(index)}
            />
          )
        ))}
      </div>

      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox} role="dialog" aria-modal="true">
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
          <button className="lightbox-nav lightbox-prev" onClick={showPrev} aria-label="Previous">‹</button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {isVideo(mediaItems[currentMediaIndex]) ? (
              <video className="lightbox-media" controls autoPlay>
                <source src={mediaItems[currentMediaIndex]} />
              </video>
            ) : (
              <img
                className="lightbox-media"
                src={mediaItems[currentMediaIndex]}
                alt={`${packageInfo.title} - Media ${currentMediaIndex + 1}`}
              />
            )}
          </div>
          <button className="lightbox-nav lightbox-next" onClick={showNext} aria-label="Next">›</button>
        </div>
      )}

      <div className="section">
        <h2 className="section-title">Overview</h2>
        <p>{packageInfo.description}</p>
        <ul className="list">
          {packageInfo.overview.highlights.map((highlight, index) => (
            <li key={index} className="list-item">{highlight}</li>
          ))}
        </ul>
      </div>

      <div className="section price-section">
        <div className="price-card">
          <div className="price-info">
            <h3 className="price-title">Package Price</h3>
            
            <div className="people-selector">
              <label className="people-label">
                Price is subject to change based on the type of vehicle, quality of the room and other requirements.
              </label>

              {showStaySelector && (
                <div className="stay-selector">
                  <button
                    type="button"
                    className={`stay-option${stayType === 'standard' ? ' active' : ''}`}
                    onClick={() => setStayType('standard')}
                  >
                    Standard
                  </button>
                  <button
                    type="button"
                    className={`stay-option${stayType === 'deluxe' ? ' active' : ''}`}
                    onClick={() => setStayType('deluxe')}
                  >
                    Deluxe
                  </button>
                </div>
              )}
              
              <div className="stepper-container">
                <button 
                  className="stepper-btn stepper-minus"
                  onClick={() => {
                    const prevSize = getPreviousGroupSize();
                    if (prevSize !== null) setNumberOfPeople(prevSize);
                  }}
                  disabled={getPreviousGroupSize() === null}
                  aria-label="Decrease number of people"
                >
                  −
                </button>
                
                <div className="stepper-display">
                  <span className="stepper-number">{numberOfPeople}</span>
                  <span className="stepper-label">People</span>
                </div>
                
                <button 
                  className="stepper-btn stepper-plus"
                  onClick={() => {
                    const nextSize = getNextGroupSize();
                    if (nextSize !== null) setNumberOfPeople(nextSize);
                  }}
                  disabled={getNextGroupSize() === null}
                  aria-label="Increase number of people"
                >
                  +
                </button>
              </div>
            </div>

            <div className="price-container">
              <span className="currency">₹</span>
              <span className="price-amount">{calculateTotalPrice().toLocaleString()}</span>
            </div>
            
            <div className="price-breakdown">
              <p className="price-per-person">
                ₹{calculatePricePerPerson().toLocaleString()} per person
                {getDiscountPercentageForPackage() > 0 && (
                  <span className="discount-badge">({getDiscountPercentageForPackage()}% OFF)</span>
                )}
              </p>
            </div>

            <div className="price-features">
              {packageInfo?.features?.map((feature, index) => (
                <span key={index} className="feature-badge">{feature}</span>
              )) || (
                <>
                  <span className="feature-badge">✓ Best Price Guarantee</span>
                  <span className="feature-badge">✓ Free Cancellation</span>
                  <span className="feature-badge">✓ Group Discounts Available</span>
                </>
              )}
            </div>
          </div>
          
          <div className="booking-actions">
            <button className="book-button primary" onClick={openQuoteForm}>Get Quote for {numberOfPeople} People</button>
          </div>
        </div>
      </div>

      <div className="section">
        <h2 className="section-title">Itinerary</h2>
        {packageInfo.itinerary.map((day) => (
          <div key={day.day} className="itinerary-item">
            <span className="day-number">Day {day.day}:</span> {day.title}
            <p>{day.description}</p>
          </div>
        ))}
      </div>

      <div className="section">
        <h2 className="section-title">Inclusions</h2>
        <ul className="list">
          {packageInfo.inclusions.map((item, index) => (
            <li key={index} className="list-item">{item}</li>
          ))}
        </ul>
      </div>

      <div className="section">
        <h2 className="section-title">Exclusions</h2>
        <ul className="list">
          {packageInfo.exclusions.map((item, index) => (
            <li key={index} className="list-item">{item}</li>
          ))}
        </ul>
      </div>

      <div className="section">
        <h2 className="section-title">FAQ</h2>
        {packageInfo.faqs.map((faq, index) => (
          <div key={index} className="faq-item">
            <h3 className="question">{faq.question}</h3>
            <p className="answer">{faq.answer}</p>
          </div>
        ))}
      </div>

      {/* Quote Form Overlay */}
      {isQuoteFormOpen && (
        <div className="quote-overlay" onClick={closeQuoteForm}>
          <div className="quote-modal" onClick={(e) => e.stopPropagation()}>
            <div className="quote-header">
              <h3>Get Your Quote</h3>
              <button className="quote-close" onClick={closeQuoteForm}>×</button>
            </div>
            
            <form className="quote-form" onSubmit={handleQuoteSubmit}>
              <div className="form-group">
                <label htmlFor="startDate">Start Date *</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={quoteFormData.startDate}
                  onChange={handleQuoteFormChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="duration">Duration *</label>
                <select
                  id="duration"
                  name="duration"
                  value={quoteFormData.duration}
                  onChange={handleQuoteFormChange}
                  required
                >
                  <option value="">Select Duration</option>
                  <option value="2 Night - 3 Days">2 Night - 3 Days</option>
                  <option value="3 Night - 4 Days">3 Night - 4 Days</option>
                  <option value="4 Night - 5 Days">4 Night - 5 Days</option>
                  <option value="5 Night - 6 Days">5 Night - 6 Days</option>
                  <option value="6 Night - 7 Days">6 Night - 7 Days</option>
                  <option value="7 Night - 8 Days">7 Night - 8 Days</option>
                  <option value="8 Night - 9 Days">8 Night - 9 Days</option>
                  <option value="9 Night - 10 Days">9 Night - 10 Days</option>
                  <option value="10 Night - 11 Days">10 Night - 11 Days</option>
                  <option value="11+ Days">11+ Days</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="children">Children (Above 5 years)</label>
                <input
                  type="number"
                  id="children"
                  name="children"
                  value={quoteFormData.children}
                  onChange={handleQuoteFormChange}
                  min="0"
                  max="10"
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="toddler">Toddler (Below 5 years)</label>
                <input
                  type="number"
                  id="toddler"
                  name="toddler"
                  value={quoteFormData.toddler}
                  onChange={handleQuoteFormChange}
                  min="0"
                  max="10"
                  placeholder="0"
                />
              </div>

              <div className="quote-summary">
                <h4>Quote Summary</h4>
                <div className="summary-item">
                  <span>Package:</span>
                  <span>{packageInfo?.title}</span>
                </div>
                <div className="summary-item">
                  <span>Adults:</span>
                  <span>{numberOfPeople}</span>
                </div>
                <div className="summary-item">
                  <span>Children:</span>
                  <span>{quoteFormData.children || '0'}</span>
                </div>
                <div className="summary-item">
                  <span>Toddler:</span>
                  <span>{quoteFormData.toddler || '0'}</span>
                </div>
                {showStaySelector && (
                  <div className="summary-item">
                    <span>Stay type:</span>
                    <span>{stayType.charAt(0).toUpperCase() + stayType.slice(1)}</span>
                  </div>
                )}
                <div className="summary-item total">
                  <span>Price per person:</span>
                  <span>₹{calculatePricePerPerson().toLocaleString()}</span>
                </div>
                <div className="summary-item total">
                  <span>Total for adults:</span>
                  <span>₹{calculateTotalPrice().toLocaleString()}</span>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={closeQuoteForm}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Send Quote Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageDetails; 